import styled from '@emotion/styled/macro'
import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { StepMenu, StepMenuItem } from '~/components/molecules/StepMenu'
import { ColorSelector } from '~/components/molecules/ColorSelector'
import { RadioButton, RadioButtonGroup } from '~/components/atoms/RadioButton'
import { PrintType } from '~/types/PrintType'
import type { Design } from '~/types/Design'
import { PrintName } from '~/components/organisms/PrintName'
import type { Color } from '~/types/Color'
import type { Customization } from '~/types/Customization'
import { mqHorizontal } from '~/utils/mediaQuery'
import { validate as validatePrintName } from '~/utils/printName'

import { Buttons } from './Buttons'
import type { Scene } from './Scene'
import type { UseSceneReturns } from './useScene'

export type { Scene } from './Scene'
export { useScene } from './useScene'

interface NavigationProps {
  // TODO: なにかの操作中は、そのアクションを引き起こした要素は `disabled` にせずローディングとして扱うべき
  //       そのため、 `mutation: "myDesign" | "cart"` のようなものにしてフォーカスが宙ぶらりんにならないようにする
  disabled?: boolean
  customization: Customization
  colors: readonly Color[]

  /**
   * カートに追加可能な状態かどうか
   */
  isReady?: boolean
  cartDisabled?: boolean

  cartButtonLabel: ReactNode

  scene: UseSceneReturns

  /**
   * AIカスタマイズ機能の有効/無効
   */
  enableAICustomization?: boolean

  onAddToCart(customization: Customization): void
  onSave(customization: Customization): void
  onShare(customization: Customization): void
  /**
   * AIカスタマイズボタンのクリックハンドラ
   */
  onAICustomize?(): void

  /**
   * 現在のパーツ用に利用可能なカラー一覧
   */
  availableColors: readonly Color[]

  /**
   * カラー選択時のハンドラ
   */
  onChangeColor(color: Color): void

  /** ロゴの印字種類の可否 */
  printLogoOptions: Record<PrintType, boolean>

  /** ロゴ印字種類の変更 */
  onChangePrintLogoType(printType: PrintType): void

  /** 名入れのプレースホルダ */
  printNamePlaceholder: string

  /** 名入れに必要なデザイン情報 */
  design: Design

  /** カスタマイズ更新 */
  onCustom(customization: Customization): void

  /**
   * アクティブなステップ項目が可視範囲に入るようスクロールを再実行するためのシグナル。
   * 値が変化したときにスクロール調整を試みる。
   */
  ensureActiveStepVisibleSignal?: number
}

export const Navigation = ({
  disabled = false,
  cartButtonLabel,
  customization,
  colors,
  isReady,
  cartDisabled = false,
  scene: { current: currentScene, scenes, change: changeScene },
  enableAICustomization = false,
  onAddToCart,
  onSave,
  onShare,
  onAICustomize,
  availableColors,
  onChangeColor,
  printLogoOptions,
  onChangePrintLogoType,
  printNamePlaceholder,
  design,
  onCustom,
  ensureActiveStepVisibleSignal = 0
}: NavigationProps) => {
  const wholeStepCount = scenes.length

  const currentStep =
    scenes.findIndex((scene) => scene.id === currentScene.id) + 1

  const scrollContainer = useRef<HTMLDivElement>(null)
  const [sceneEls] = useState<Map<Scene, HTMLElement | null>>(() => new Map())

  // 選択されているものが変わった/シグナルが更新されたらスクロールする
  useEffect(() => {
    // 依存のシグナルを参照（Lint抑制兼、意図の明確化）
    void ensureActiveStepVisibleSignal
    // レイアウト完了後に走らせる
    const raf = requestAnimationFrame(() => {
      // refs適用やCSS計算が完了する次フレームで実行
      const raf2 = requestAnimationFrame(() => {
        const fallbackContainer = scrollContainer.current
        // 優先: マップから現在シーンの要素を取得。なければARIAからフォールバック
        const mapped = sceneEls.get(currentScene) || null
        const ariaButton = fallbackContainer?.querySelector(
          "button[aria-current='step']"
        ) as HTMLButtonElement | null
        const el =
          mapped ??
          (ariaButton
            ? (ariaButton.closest('li') as HTMLElement | null) ?? ariaButton
            : null)
        if (!el) return

        // 実際にスクロールしている要素（最も近いスクロール親）を探索
        const findScrollableParent = (start: HTMLElement | null) => {
          let node: HTMLElement | null = start
          while (node && node !== document.body) {
            const style = getComputedStyle(node)
            const canScrollY =
              (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
              node.scrollHeight > node.clientHeight
            const canScrollX =
              (style.overflowX === 'auto' || style.overflowX === 'scroll') &&
              node.scrollWidth > node.clientWidth
            if (canScrollX || canScrollY) {
              return node
            }
            node = node.parentElement
          }
          return null
        }

        const container =
          findScrollableParent(el.parentElement as HTMLElement | null) ||
          fallbackContainer
        if (!container) return

        // コンテンツ領域とスクロール領域のサイズが違う = オーバーフローしている
        const contRect = container.getBoundingClientRect()
        const elRect = el.getBoundingClientRect()

        const hasHorizontalOverflow =
          container.scrollWidth > container.clientWidth
        if (hasHorizontalOverflow) {
          const delta =
            elRect.left -
            contRect.left +
            el.clientWidth / 2 -
            container.clientWidth / 2
          const targetLeft = Math.max(
            0,
            Math.min(container.scrollLeft + delta, container.scrollWidth)
          )
          container.scroll({ left: targetLeft, behavior: 'smooth' })
          return
        }

        // 縦方向（デスクトップ）
        const deltaY =
          elRect.top -
          contRect.top +
          el.clientHeight / 2 -
          container.clientHeight / 2
        const targetTop = Math.max(
          0,
          Math.min(container.scrollTop + deltaY, container.scrollHeight)
        )
        container.scroll({ top: targetTop, behavior: 'smooth' })
      })

      return () => cancelAnimationFrame(raf2)
    })

    return () => cancelAnimationFrame(raf)
  }, [currentScene, sceneEls, ensureActiveStepVisibleSignal])

  const onNext = useCallback(() => {
    // `currentStep` はインデックスに+1された値なので、そのまま参照すると次の要素になる
    const next = scenes[currentStep]

    if (!next) {
      return
    }

    changeScene(next)
  }, [changeScene, scenes, currentStep])

  return (
    <Container>
      {currentScene.type === 'part' ? (
        <ColorSelectorContainer>
          <ColorSelector
            disabled={disabled}
            value={customization.parts[currentScene.part.id]?.color ?? null}
            colors={availableColors}
            onChange={onChangeColor}
          />
        </ColorSelectorContainer>
      ) : currentScene.type === 'print_logo' ? (
        <SelectorContainer>
          <LogoAction>
            <RadioButtonGroup
              value={
                customization.printLogo?.printType ??
                (customization.printLogo?.printType === null ? 'null' : '')
              }
              onValueChange={(value: PrintType | 'null') => {
                onChangePrintLogoType(
                  value === 'null' ? (null as unknown as PrintType) : value
                )
              }}
            >
              {printLogoOptions[PrintType.GoldPressing] && (
                <RadioButton value={PrintType.GoldPressing} disabled={disabled}>
                  ゴールド
                </RadioButton>
              )}
              {printLogoOptions[PrintType.SilverPressing] && (
                <RadioButton
                  value={PrintType.SilverPressing}
                  disabled={disabled}
                >
                  シルバー
                </RadioButton>
              )}
              {printLogoOptions[PrintType.Embossing] && (
                <RadioButton value={PrintType.Embossing} disabled={disabled}>
                  型押し
                </RadioButton>
              )}
              <RadioButton value="null" disabled={disabled}>
                なし
              </RadioButton>
            </RadioButtonGroup>
          </LogoAction>
        </SelectorContainer>
      ) : currentScene.type === 'print_name' ? (
        <SelectorContainer>
          <PrintNameWithSpaces
            disabled={disabled}
            design={design}
            customization={customization}
            placeholder={printNamePlaceholder}
            onCustom={onCustom}
          />
        </SelectorContainer>
      ) : null}
      <MenuContainer ref={scrollContainer}>
        <StepMenu>
          {scenes.map((scene) => {
            const ref = (el: HTMLLIElement) => {
              sceneEls.set(scene, el)
            }

            switch (scene.type) {
              case 'part': {
                const partCustomization = customization.parts[scene.part.id]

                const color = colors.find(
                  (c) => c.id === partCustomization?.color.id
                )

                return (
                  <StepMenuItemWithPadding
                    ref={ref}
                    key={scene.id}
                    disabled={disabled}
                    selected={scene.id === currentScene.id}
                    status={
                      color ? `completed:${color.colorCode}` : 'not_completed'
                    }
                    onClick={() => changeScene(scene)}
                  >
                    {scene.part.name}
                  </StepMenuItemWithPadding>
                )
              }
              case 'print_logo':
                return (
                  <StepMenuItemWithPadding
                    ref={ref}
                    key={scene.id}
                    disabled={disabled}
                    selected={scene.id === currentScene.id}
                    status={
                      customization.printLogo === null
                        ? 'not_completed'
                        : 'completed'
                    }
                    onClick={() => changeScene(scene)}
                  >
                    ロゴ
                    {customization.printLogo?.printType === null
                      ? '（なし）'
                      : ''}
                  </StepMenuItemWithPadding>
                )
              case 'print_name': {
                const isReady =
                  typeof customization.printName !== 'undefined' &&
                  (customization.printName === null ||
                    customization.printName.printType === null ||
                    (customization.printName &&
                      validatePrintName(customization.printName.text).ok))

                return (
                  <StepMenuItemWithPadding
                    ref={ref}
                    key={scene.id}
                    disabled={disabled}
                    selected={scene.id === currentScene.id}
                    status={isReady ? 'completed' : 'not_completed'}
                    onClick={() => changeScene(scene)}
                  >
                    名入れ刻印
                  </StepMenuItemWithPadding>
                )
              }
            }
          })}
        </StepMenu>
      </MenuContainer>
      <Buttons
        state={
          currentStep < wholeStepCount
            ? isReady
              ? 'both'
              : 'next-only'
            : 'cart-only'
        }
        disabled={disabled}
        cartDisabled={disabled || !isReady || cartDisabled}
        nextButtonLabel={
          <>
            次へ ({currentStep}/{wholeStepCount})
          </>
        }
        cartButtonLabel={cartButtonLabel}
        onClickNextButton={() => onNext()}
        onClickCartButton={() => onAddToCart(customization)}
      />
    </Container>
  )
}

const Container = styled.div`
  --_padding: var(--gutter-medium);
  --_gap: var(--_padding);

  display: flex;
  flex-direction: column;
  padding: 0; /* move padding to inner sections to allow sticky footer */
  gap: 0;

  ${mqHorizontal} {
    height: 100%;
  }
`

const SelectorContainer = styled.div`
  padding: var(--gutter-medium);
  border-bottom: 1px solid var(--gray-200);
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ColorSelectorContainer = styled.div`
  padding: var(--gutter-medium) 0;
  border-bottom: 1px solid var(--gray-200);
`

const MenuContainer = styled.div`
  flex: 1 1 auto;
  overflow-x: auto;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: var(--gutter-large);

  ${mqHorizontal} {
    overflow-x: hidden;
    overflow-y: auto;
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const StepMenuItemWithPadding = styled(StepMenuItem)`
  &:last-child::after {
    content: '';

    display: block;
    margin-top: -1px;
    height: 1px;
    width: calc(100% + var(--_padding));

    pointer-events: none;
    visibility: hidden;

    ${mqHorizontal} {
      display: none;
    }
  }
`

const LogoAction = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const PlaceholderLabel = styled.span`
  &[data-hidden] {
    visibility: hidden;
  }
`

const PrintNameWithSpaces = styled(PrintName)`
  width: 100%;
`
